import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Search,
  BookOpen,
  Filter,
  ArrowRight,
  Sparkles,
  ChevronLeft,
  ChevronRight,
  Layers3,
} from "lucide-react";
import { GLOSSARY, type GlossaryTerm } from "../data/glossary";

const categories: Array<GlossaryTerm["category"] | "All"> = [
  "All",
  "Workflow",
  "Printing",
  "Ink",
  "White Ink",
  "Film",
  "Powder",
  "Curing",
  "Pressing",
  "Color/RIP",
  "Artwork",
  "Maintenance",
  "Quality",
  "Environment",
  "Business",
];

function escapeRegExp(s: string) {
  return s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

function renderDefinitionWithLinks(
  text: string,
  termMap: Map<string, GlossaryTerm>,
  openTerm: (t: GlossaryTerm) => void
) {
  const terms = Array.from(termMap.values())
    .filter((t) => t.term.length >= 4)
    .sort((a, b) => b.term.length - a.term.length);

  if (!terms.length) return text;

  const pattern = terms.map((t) => escapeRegExp(t.term)).join("|");
  const re = new RegExp(`\\b(${pattern})\\b`, "gi");

  const parts: React.ReactNode[] = [];
  let last = 0;

  for (const match of text.matchAll(re)) {
    const index = match.index ?? 0;
    const found = match[0];

    if (index > last) parts.push(text.slice(last, index));

    parts.push(
      <button
        key={`${index}-${found}`}
        type="button"
        onClick={() => {
          const hit = termMap.get(found.toLowerCase());
          if (hit) openTerm(hit);
        }}
        className="font-semibold text-dtf-blue transition hover:text-dtf-accent hover:underline"
      >
        {found}
      </button>
    );

    last = index + found.length;
  }

  if (last < text.length) parts.push(text.slice(last));

  return <>{parts}</>;
}

function BluePanel({
  children,
  className = "",
}: {
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <div
      className={[
        "relative overflow-hidden rounded-[28px] border border-dtf-accent/20",
        "bg-[radial-gradient(circle_at_top_center,rgba(37,99,235,0.18),transparent_42%),linear-gradient(180deg,rgba(7,16,35,0.98)_0%,rgba(6,14,30,0.98)_100%)]",
        "shadow-[0_0_0_1px_rgba(245,158,11,0.05),0_0_34px_rgba(37,99,235,0.16)]",
        className,
      ].join(" ")}
    >
      <div className="pointer-events-none absolute inset-0 bg-[linear-gradient(90deg,rgba(37,99,235,0.08),transparent_30%,transparent_70%,rgba(37,99,235,0.06))]" />
      <div className="pointer-events-none absolute inset-0 rounded-[28px] ring-1 ring-inset ring-white/5" />
      {children}
    </div>
  );
}

function StatPill({
  icon,
  label,
  value,
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
}) {
  return (
    <div className="rounded-2xl border border-white/10 bg-black/20 px-4 py-3">
      <div className="mb-1 flex items-center gap-2 text-xs uppercase tracking-[0.16em] text-dtf-text-muted">
        <span className="text-dtf-accent">{icon}</span>
        <span>{label}</span>
      </div>
      <div className="text-base font-semibold text-white">{value}</div>
    </div>
  );
}

function CategoryChip({
  active,
  children,
  onClick,
}: {
  active: boolean;
  children: React.ReactNode;
  onClick: () => void;
}) {
  return (
    <button
      onClick={onClick}
      className={`rounded-full border px-3 py-1.5 text-sm font-medium transition ${
        active
          ? "border-dtf-blue/35 bg-dtf-blue/10 text-white shadow-[0_0_18px_rgba(59,130,246,0.16)]"
          : "border-white/10 bg-black/20 text-dtf-text-muted hover:border-dtf-blue/30 hover:text-white"
      }`}
    >
      {children}
    </button>
  );
}

function GlossaryCard({
  term,
  onOpen,
}: {
  term: GlossaryTerm;
  onOpen: () => void;
}) {
  return (
    <button
      onClick={onOpen}
      className="group relative overflow-hidden rounded-[26px] border border-white/10 bg-[radial-gradient(circle_at_top_center,rgba(37,99,235,0.14),transparent_48%),linear-gradient(180deg,rgba(9,21,44,0.98)_0%,rgba(7,15,31,0.98)_100%)] p-5 text-left transition duration-200 hover:-translate-y-0.5 hover:border-dtf-blue/30 hover:shadow-[0_0_26px_rgba(37,99,235,0.18)]"
    >
      <div className="pointer-events-none absolute inset-0 bg-[linear-gradient(90deg,rgba(37,99,235,0.06),transparent_35%,transparent_65%,rgba(37,99,235,0.04))]" />

      <div className="relative">
        <div className="mb-4 flex items-start justify-between gap-3">
          <div className="min-w-0 flex-1">
            <div className="inline-flex rounded-full border border-dtf-accent/20 bg-black/20 px-3 py-1 text-xs font-semibold uppercase tracking-[0.16em] text-dtf-accent">
              {term.category}
            </div>

            <div className="mt-3 text-2xl font-bold tracking-tight text-white transition group-hover:text-dtf-blue">
              {term.term}
            </div>
          </div>

          <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl border border-white/10 bg-black/20 text-dtf-blue transition group-hover:border-dtf-blue/30 group-hover:bg-dtf-blue/10">
            <ArrowRight className="h-5 w-5" />
          </div>
        </div>

        <div className="rounded-2xl border border-white/8 bg-black/18 p-4">
          <div className="mb-2 inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.2em] text-dtf-blue">
            <Sparkles className="h-3.5 w-3.5" />
            Quick Look
          </div>

          <p className="line-clamp-3 text-sm leading-7 text-dtf-text">
            {term.definition}
          </p>
        </div>

        {term.tags?.length ? (
          <div className="mt-4 flex flex-wrap gap-2">
            {term.tags.slice(0, 4).map((tag) => (
              <span
                key={tag}
                className="rounded-full border border-white/10 bg-black/20 px-3 py-1 text-xs text-dtf-text-muted"
              >
                #{tag}
              </span>
            ))}
          </div>
        ) : null}
      </div>
    </button>
  );
}

export function Glossary() {
  const navigate = useNavigate();

  const [q, setQ] = useState("");
  const [cat, setCat] = useState<(typeof categories)[number]>("All");
  const [active, setActive] = useState<GlossaryTerm | null>(null);

  const [nav, setNav] = useState<{ stack: GlossaryTerm[]; index: number }>({
    stack: [],
    index: -1,
  });

  const termMap = useMemo(() => {
    const m = new Map<string, GlossaryTerm>();
    for (const t of GLOSSARY) m.set(t.term.toLowerCase(), t);
    return m;
  }, []);

  const openTerm = (t: GlossaryTerm) => {
    setActive(t);

    setNav((prev) => {
      const trimmed = prev.stack.slice(0, prev.index + 1);
      const last = trimmed[trimmed.length - 1];

      if (last && last.term === t.term) return prev;

      const nextStack = [...trimmed, t];
      return { stack: nextStack, index: nextStack.length - 1 };
    });
  };

  const goBack = () => {
    setNav((prev) => {
      const nextIndex = Math.max(0, prev.index - 1);
      const t = prev.stack[nextIndex];
      if (t) setActive(t);
      return { ...prev, index: nextIndex };
    });
  };

  const goForward = () => {
    setNav((prev) => {
      const nextIndex = Math.min(prev.stack.length - 1, prev.index + 1);
      const t = prev.stack[nextIndex];
      if (t) setActive(t);
      return { ...prev, index: nextIndex };
    });
  };

  const closeModal = () => {
    setActive(null);
    setNav({ stack: [], index: -1 });
  };

  const applyCategoryFilter = (category: GlossaryTerm["category"]) => {
    setCat(category);
    setQ("");
    closeModal();
  };

  const getNextRouteForCategory = (category: GlossaryTerm["category"]) => {
    if (category === "Maintenance" || category === "White Ink") return "/maintenance";
    if (
      category === "Printing" ||
      category === "Quality" ||
      category === "Film" ||
      category === "Powder" ||
      category === "Pressing" ||
      category === "Curing"
    ) {
      return "/issues";
    }
    if (category === "Color/RIP" || category === "Artwork") return "/cards";
    return "/diagnosis";
  };

  const filtered = useMemo(() => {
    const query = q.trim().toLowerCase();

    return GLOSSARY
      .filter((t) => (cat === "All" ? true : t.category === cat))
      .filter((t) => {
        if (!query) return true;
        const blob = [
          t.term,
          t.definition,
          t.category,
          ...(t.tags ?? []),
          ...(t.related ?? []),
        ]
          .join(" ")
          .toLowerCase();

        return blob.includes(query);
      })
      .sort((a, b) => a.term.localeCompare(b.term));
  }, [q, cat]);

  return (
    <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
      <BluePanel className="mb-6">
        <div className="p-6 sm:p-7">
          <div className="flex flex-col gap-6 lg:flex-row lg:items-start lg:justify-between">
            <div className="max-w-3xl">
              <div className="mb-3 inline-flex items-center gap-2 rounded-full border border-dtf-accent/20 bg-dtf-blue/10 px-4 py-1.5 text-xs font-semibold uppercase tracking-[0.22em] text-dtf-blue">
                <BookOpen className="h-3.5 w-3.5" />
                Glossary
              </div>

              <h1 className="mb-3 text-4xl font-extrabold tracking-tight text-white sm:text-5xl">
                Find a term fast
              </h1>

              <p className="text-lg leading-8 text-dtf-text">
                Search DTF terms, browse by category, and open any card for the full definition,
                related terms, and quick navigation to the most relevant section.
              </p>
            </div>

            <div className="grid gap-3 sm:min-w-[280px] sm:grid-cols-2">
              <StatPill
                icon={<Layers3 className="h-4 w-4" />}
                label="Showing Results"
                value={`${filtered.length} of ${GLOSSARY.length}`}
              />
              <StatPill
                icon={<Filter className="h-4 w-4" />}
                label="Category"
                value={cat}
              />
            </div>
          </div>
        </div>
      </BluePanel>

      <BluePanel className="mb-6">
        <div className="border-b border-white/8 px-6 py-5">
          <div className="mb-2 inline-flex items-center gap-2 rounded-full border border-dtf-blue/25 bg-dtf-blue/10 px-4 py-1.5 text-xs font-semibold uppercase tracking-[0.22em] text-dtf-blue">
            <Search className="h-3.5 w-3.5" />
            Search
          </div>

          <h2 className="text-3xl font-bold tracking-tight text-white">Find a term fast</h2>
          <p className="mt-2 max-w-3xl text-dtf-text-muted">
            Search by term name, definition, tag, or category to narrow down the glossary quickly.
          </p>
        </div>

        <div className="p-6">
          <div className="flex items-center gap-3 rounded-2xl border border-white/10 bg-black/20 px-4 py-4 transition focus-within:border-dtf-blue/35">
            <Search className="h-5 w-5 text-dtf-text-muted" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search terms by name, definition, or tag..."
              className="w-full bg-transparent text-white placeholder:text-dtf-text-muted outline-none"
            />
          </div>

          <div className="mt-5">
            <div className="mb-3 inline-flex items-center gap-2 rounded-full border border-dtf-blue/25 bg-dtf-blue/10 px-4 py-1.5 text-xs font-semibold uppercase tracking-[0.22em] text-dtf-blue">
              <Filter className="h-3.5 w-3.5" />
              Filters
            </div>

            <div className="flex flex-wrap gap-2">
              {categories.map((c) => (
                <CategoryChip
                  key={c}
                  active={c === cat}
                  onClick={() => setCat(c)}
                >
                  {c}
                </CategoryChip>
              ))}
            </div>
          </div>
        </div>
      </BluePanel>

      <BluePanel className="mb-6">
        <div className="flex items-center justify-between gap-4 px-6 py-5">
          <div>
            <div className="text-sm text-dtf-text-muted">Showing results</div>
            <div className="text-2xl font-bold text-white">
              {filtered.length} of {GLOSSARY.length} terms
            </div>
          </div>

          <div className="rounded-full border border-dtf-blue/25 bg-dtf-blue/10 px-4 py-2 text-sm font-semibold uppercase tracking-[0.18em] text-dtf-blue">
            Glossary visible
          </div>
        </div>
      </BluePanel>

      <div className="grid gap-5 sm:grid-cols-2 xl:grid-cols-3">
        {filtered.map((t) => (
          <GlossaryCard key={t.term} term={t} onOpen={() => openTerm(t)} />
        ))}
      </div>

      {active && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/75 p-4 backdrop-blur-sm"
          onClick={closeModal}
        >
          <BluePanel className="w-full max-w-3xl" >
            <div onClick={(e) => e.stopPropagation()}>
              <div className="border-b border-white/8 px-6 py-6">
                <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
                  <div className="min-w-0">
                    <div className="inline-flex rounded-full border border-dtf-accent/20 bg-black/20 px-3 py-1 text-xs font-semibold uppercase tracking-[0.16em] text-dtf-accent">
                      {active.category}
                    </div>

                    <div className="mt-4 text-3xl font-bold leading-tight text-white sm:text-4xl">
                      {active.term}
                    </div>
                  </div>

                  <div className="flex items-center gap-3 sm:flex-col sm:items-end">
                    <div className="rounded-full border border-white/10 bg-black/20 px-3 py-1 text-xs text-dtf-text-muted">
                      {Math.max(1, nav.index + 1)}/{Math.max(1, nav.stack.length)}
                    </div>

                    <button
                      type="button"
                      onClick={() => applyCategoryFilter(active.category)}
                      className="rounded-xl border border-white/10 bg-black/20 px-4 py-2 text-sm font-medium text-white transition hover:border-dtf-blue/30 hover:bg-black/30"
                      title="View all terms in this category"
                    >
                      View Category
                    </button>
                  </div>
                </div>
              </div>

              <div className="px-6 pt-5">
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={goBack}
                    disabled={nav.index <= 0}
                    className="inline-flex items-center gap-2 rounded-xl border border-white/10 bg-black/20 px-4 py-2 text-sm font-medium text-white transition hover:border-dtf-blue/30 hover:bg-black/30 disabled:opacity-40"
                  >
                    <ChevronLeft className="h-4 w-4" />
                    Back
                  </button>

                  <button
                    type="button"
                    onClick={goForward}
                    disabled={nav.index >= nav.stack.length - 1}
                    className="inline-flex items-center gap-2 rounded-xl border border-white/10 bg-black/20 px-4 py-2 text-sm font-medium text-white transition hover:border-dtf-blue/30 hover:bg-black/30 disabled:opacity-40"
                  >
                    Forward
                    <ChevronRight className="h-4 w-4" />
                  </button>
                </div>
              </div>

              <div className="px-6 pb-6 pt-5">
                <div className="rounded-[24px] border border-white/10 bg-black/18 p-6 shadow-[0_0_20px_rgba(37,99,235,0.10)]">
                  <div className="mb-3 inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.2em] text-dtf-blue">
                    <Sparkles className="h-3.5 w-3.5" />
                    Definition
                  </div>

                  <div className="text-lg leading-8 text-white">
                    {renderDefinitionWithLinks(active.definition, termMap, openTerm)}
                  </div>
                </div>

                <div className="mt-5">
                  <button
                    type="button"
                    onClick={() => {
                      const route = getNextRouteForCategory(active.category);
                      closeModal();
                      navigate(route);
                    }}
                    className="w-full rounded-2xl bg-dtf-accent px-5 py-3.5 font-semibold text-dtf-dark transition hover:bg-dtf-accent-hover"
                  >
                    Open Related Section
                  </button>
                </div>

                {(active.tags?.length || active.related?.length) && (
                  <div className="mt-5 space-y-5">
                    {active.tags?.length ? (
                      <div>
                        <div className="mb-2 text-sm text-dtf-text-muted">Tags</div>
                        <div className="flex flex-wrap gap-2">
                          {active.tags.map((tag) => (
                            <span
                              key={tag}
                              className="rounded-full border border-white/10 bg-black/20 px-3 py-1 text-xs text-dtf-text-muted"
                            >
                              #{tag}
                            </span>
                          ))}
                        </div>
                      </div>
                    ) : null}

                    {active.related?.length ? (
                      <div>
                        <div className="mb-2 text-sm text-dtf-text-muted">Related Terms</div>
                        <div className="flex flex-wrap gap-2">
                          {active.related.map((name) => (
                            <button
                              key={name}
                              type="button"
                              onClick={() => {
                                const hit = termMap.get(name.toLowerCase());
                                if (hit) openTerm(hit);
                              }}
                              className="rounded-full border border-white/10 bg-black/20 px-3 py-1 text-xs text-dtf-text-muted transition hover:border-dtf-blue/30 hover:text-dtf-blue"
                            >
                              {name}
                            </button>
                          ))}
                        </div>
                      </div>
                    ) : null}
                  </div>
                )}

                <button
                  onClick={closeModal}
                  className="mt-6 w-full rounded-2xl border border-white/10 bg-black/20 px-5 py-3 font-medium text-white transition hover:border-dtf-blue/30 hover:bg-black/30"
                >
                  Close
                </button>
              </div>
            </div>
          </BluePanel>
        </div>
      )}
    </div>
  );
}